<script setup lang="ts">
import {computed, reactive, ref, useTemplateRef, watch} from "vue";
import {Button, Card, DatePicker, InputText, Label, Menu} from "@/Components/Library";
import GroupSelect from "./GroupSelect.vue";
import {getAdminGroups} from "@/Services/groups";
import {RouterLink, useRouter} from "vue-router";
import {PageLoading, PageTitle} from "@/Components/Global";
import LaunchHeader from "./LaunchHeader.vue";
import {Icon} from "@iconify/vue";
import {saveGroupQuestions} from "@/Services/group-questions";
import {useToast} from "primevue/usetoast";
import {useStorage, watchDebounced} from "@vueuse/core";
import User from "@/Models/User";
import {duplicateQuestion, getLaunchQuestions} from "@/Services/questions";
import dayjs from "dayjs";
import {useMainStore} from "@/Stores/store";
import QuestionItem from "../QuestionItem.vue";
import {useConfirm} from "primevue/useconfirm";
import GroupItem from "../../Groups/GroupItem.vue";
import type {Question} from "@/Types/question";

const $user = new User();
const $store = useMainStore();
const toast = useToast();
const router = useRouter();
const confirm = useConfirm();

const currentRoute = router.currentRoute;
if (currentRoute.value.query.question) {
    $store.launch.questions.push(currentRoute.value.query.question);
}
if (currentRoute.value.query.questions) {
    $store.launch.questions.push(...currentRoute.value.query.questions);
}
const state = reactive({
    loading: {
        questions: false,
        groups: false,
    },
});
// Sort — shared with QuestionsIndex via the same localStorage key
const sort = useStorage("questions.index.sort", "title");
const sortMenuItems = ref([
    {
        label: "Alphabetical",
        icon: "mdi:sort-alphabetical-ascending",
        value: "title",
        command: () => {
            sort.value = "title";
        },
    },
    {
        label: "Most recent",
        icon: "ic:round-edit-calendar",
        value: "-updated_at",
        command: () => {
            sort.value = "-updated_at";
        },
    },
    {
        label: "Least recent",
        icon: "ic:round-edit-calendar",
        value: "updated_at",
        command: () => {
            sort.value = "updated_at";
        },
    },
]);
const sortPanel = useTemplateRef("sortPanel");
const selectedSortOption = computed(() => sortMenuItems.value.find((item) => item.value === sort.value));

// Reorder mode state
const reorderMode = ref(false);
const questions = ref<Question[]>([]);

const getQuestions = async () => {
    try {
        state.loading.questions = true;
        const {data} = await getLaunchQuestions({questions: $store.launch.questions, sort: sort.value});
        questions.value = data;
        // Sync store to server-sorted order
        $store.launch.questions = questions.value.map((q) => q.id);
    } catch (e) {
        console.log(e);
    } finally {
        state.loading.questions = false;
    }
};
getQuestions();

// Re-fetch with new sort when sort changes (only outside of reorder mode)
watch(sort, () => {
    if (!reorderMode.value) {
        getQuestions();
    }
});

const defaultLaunch: {
    group_id: number | null;
    publish_from: Date | null;
    publish_to: Date | null;
} = {
    group_id: currentRoute.value.query.group,
    publish_from: dayjs().toDate(),
    publish_to: dayjs().add(8, "days").hour(0).minute(0).toDate(),
};
const launch = useStorage(`U${$user.id}:launchForm`, defaultLaunch);
if (launch.value?.publish_from && dayjs(launch.value?.publish_from).isBefore(dayjs())) {
    launch.value.publish_from = dayjs().toDate();
}
if (launch.value?.publish_to && dayjs(launch.value?.publish_to).isSameOrBefore(dayjs(launch.value?.publish_from))) {
    launch.value.publish_to = dayjs(launch.value.publish_from).add(8, "days").hour(0).minute(0).toDate();
}
if (currentRoute.value.query.group) {
    launch.value.group_id = currentRoute.value.query.group;
}
const search = reactive({
    groups: "",
});
const groups = ref([]);
const selectedGroup = computed(
    () => groups.value.find((G) => G.id === launch.value.group_id || G.id === currentRoute.value.query.group) ?? null,
);
const selectedQuestion = ref();
const getGroups = async () => {
    try {
        console.log($store.launch.user);
        state.loading.groups = true;
        const {data} = await getAdminGroups({
            filter: {
                search: search.groups,
                user: $store.launch.user,
            },
            include: ["user", "group_questionsCount", "membersCount"],
        });

        groups.value = data;
    } catch (e) {
        console.log(e);
    } finally {
        state.loading.groups = false;
    }
};
if (!selectedGroup.value) {
    getGroups();
}

const save = () => {
    if (!launch.value.publish_from) {
        toast.add({
            severity: "warn",
            summary: "Publish from date is required",
            detail: "Please select a date to publish the question",
            life: 3000,
        });
    }
    if (!launch.value.publish_to) {
        toast.add({
            severity: "warn",
            summary: "Publish to date is required",
            detail: "Please select a deadline for the question",
            life: 3000,
        });
    }
    if (!launch.value.publish_from || !launch.value.publish_to || !selectedGroup.value) {
        return;
    }

    saveGroupQuestions(selectedGroup.value.id, launch.value, {questions: $store.launch.questions}).then(({data}) => {
        const launchGroup = selectedGroup.value;
        toast.add({
            severity: "success",
            summary: "Question launched",
            detail: "The question has been successfully launched",
            life: 3000,
        });
        $store.group.questions = [];
        $store.feed.questions = [];
        $store.launch.questions = [];
        launch.value.group_id = null;
        router.push({
            name: "feed",
            params: {group: launchGroup.id},
        });
    });
};

const resetGroup = () => {
    launch.value.group_id = null;
    router.push({query: {launch: true}});
};

const cancel = () => {
    $store.launch.questions = [];
    $store.launch.user = null;
    router.push({name: "questions.index"});
};

const questionMenuContainer = useTemplateRef("questionMenuContainer");
const questionMenuItems = ref([
    {
        label: "Edit",
        icon: "prime:pencil",
        command: () => {
            if (selectedQuestion.value.question.groups_count) {
                confirm.require({
                    header: "Editing a published question",
                    message: "This question has already been published. Would you like to duplicate it?",
                    acceptClass: "bg-askr-green text-white px-2 py-1 rounded-lg hover:brightness-110",
                    rejectClass: "text-red-600 px-2 py-1 rounded-lg hover:brightness-110",
                    accept: async () => {
                        const {data} = await duplicateQuestion(selectedQuestion.value.question.id);
                        questions.value = questions.value.filter((T) => T.id !== selectedQuestion.value.id);
                        toast.add({
                            severity: "success",
                            summary: "Duplicated",
                            detail: "Question duplicated successfully.",
                            life: 3000,
                        });
                        await router.push({name: "questions.edit", params: {question: data.id}});
                    },
                });
                questionMenuContainer.value.menu.toggle();
                return;
            }

            router.push({name: "questions.edit", params: {question: selectedQuestion.value.id}});
        },
    },
]);

const toggleQuestionMenu = (e, question) => {
    selectedQuestion.value = question;
    questionMenuContainer.value.menu.toggle(e);
};

// Reorder helpers: swap items and keep $store.launch.questions in sync
const swapQuestions = (indexA: number, indexB: number) => {
    if (indexA < 0 || indexB < 0 || indexA >= questions.value.length || indexB >= questions.value.length) {
        return;
    }

    const newQuestions = [...questions.value];
    newQuestions.splice(indexB, 0, newQuestions.splice(indexA, 1)[0]);
    questions.value = newQuestions;
    // update store order
    $store.launch.questions = questions.value.map((q) => q.id);
};

const moveUp = (index: number) => {
    if (index <= 0) {
        return;
    }
    swapQuestions(index, index - 1);
};

const moveDown = (index: number) => {
    if (index >= questions.value.length - 1) {
        return;
    }
    swapQuestions(index, index + 1);
};

const moveToTop = (index: number) => {
    if (index <= 0) {
        return;
    }
    const newQuestions = [...questions.value];
    newQuestions.unshift(newQuestions.splice(index, 1)[0]);
    questions.value = newQuestions;
    $store.launch.questions = questions.value.map((q) => q.id);
};

const moveToBottom = (index: number) => {
    if (index >= questions.value.length - 1) {
        return;
    }
    const newQuestions = [...questions.value];
    newQuestions.push(newQuestions.splice(index, 1)[0]);
    questions.value = newQuestions;
    $store.launch.questions = questions.value.map((q) => q.id);
};
const toggleReorderMode = () => {
    reorderMode.value = !reorderMode.value;
    // ensure store has current order when toggling off
    if (!reorderMode.value) {
        $store.launch.questions = questions.value.map((q) => q.id);
    }
};

watch(
    () => $store.launch.questions,
    (nQuestions) => {
        console.log(nQuestions, $store.launch.questions, questions.value);
        questions.value = questions.value.filter((T) => nQuestions.includes(T.id));
    },
);
const groupsBlock = useTemplateRef("groupsBlock");
watch(selectedGroup, (nVal, oVal) => {
    setTimeout(() => {
        groupsBlock.value.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    }, 300);
});

watchDebounced(() => search.groups, getGroups, {debounce: 1000});
</script>
<template>
    <div class="mx-auto w-full max-w-7xl pb-12">
        <div class="m-container" v-auto-animate="{duration: 100}">
            <LaunchHeader
                class="mb-2 rounded-md bg-askr-green p-3 text-white"
                v-if="currentRoute.query.launch"
                @cancel="cancel"
            >
                <template #header>
                    <div class="ml-4 flex">
                        <p>Choose a group and then set your publish dates</p>
                    </div>
                </template>
            </LaunchHeader>
            <PageTitle title="Launch Questions" class="mb-1 py-1">
                <div class="ml-auto flex items-center gap-2">
                    <Button
                        :icon="{icon: reorderMode ? 'mdi:check' : 'mdi:swap-vertical', width: 24}"
                        @click="toggleReorderMode"
                    >
                        {{ reorderMode ? "Done" : "Reorder" }}
                    </Button>
                    <div class="relative">
                        <Button
                            :icon="{icon: 'iconoir:sort', width: 26}"
                            :rounded="false"
                            :disabled="reorderMode"
                            class="aspect-square h-full flex-none bg-askr-green !p-2.5 text-white hover:brightness-110"
                            @click="sortPanel.menu.toggle($event)"
                        />
                        <div
                            v-if="selectedSortOption"
                            class="pointer-events-none absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500"
                        >
                            <Icon :icon="selectedSortOption.icon" class="h-3 w-3 text-white" />
                        </div>
                    </div>
                    <Menu ref="sortPanel" :popup="true" :model="sortMenuItems" />
                </div>
            </PageTitle>
            <template v-if="questions.length">
                <div class="mb-8" v-auto-animate>
                    <!-- Reorder mode: single column with move controls -->
                    <div v-if="reorderMode" class="mx-auto flex w-full max-w-3xl flex-col gap-2" v-auto-animate>
                        <template v-for="(question, index) in questions" :key="question.id">
                            <div class="flex items-start gap-3">
                                <div class="flex flex-col items-center">
                                    <Button
                                        v-tooltip.top="'Move to top'"
                                        :icon="{icon: 'bxs:chevrons-up', width: '20'}"
                                        class="!px-3 !py-4"
                                        :disabled="index === 0"
                                        @click="moveToTop(index)"
                                    />
                                    <Button
                                        v-tooltip.top="'Move up'"
                                        :icon="{icon: 'bxs:up-arrow', width: '20'}"
                                        class="!px-3 !py-4"
                                        :disabled="index === 0"
                                        @click="moveUp(index)"
                                    />
                                    <span
                                        class="flex h-8 w-8 items-center justify-center rounded-full bg-askr-green text-lg font-bold text-white"
                                    >
                                        {{ index + 1 }}
                                    </span>
                                    <Button
                                        v-tooltip.bottom="'Move down'"
                                        :icon="{icon: 'bxs:down-arrow', width: '20'}"
                                        class="!px-3 !py-4"
                                        :disabled="index === questions.length - 1"
                                        @click="moveDown(index)"
                                    />
                                    <Button
                                        v-tooltip.bottom="'Move to bottom'"
                                        :icon="{icon: 'bxs:chevrons-down', width: '20'}"
                                        class="!px-3 !py-4"
                                        :disabled="index === questions.length - 1"
                                        @click="moveToBottom(index)"
                                    />
                                </div>
                                <div class="flex-1">
                                    <QuestionItem
                                        :question="question"
                                        :show-menu="question.user_id === $user.id"
                                        @toggleQuestionMenu="toggleQuestionMenu"
                                    />
                                </div>
                            </div>
                        </template>
                    </div>
                    <!-- Normal mode: grid -->
                    <div v-else class="grid grid-cols-2 gap-2 lg:grid-cols-3">
                        <template v-for="question in questions" :key="question.id">
                            <QuestionItem
                                :question="question"
                                :show-menu="question.user_id === $user.id"
                                @toggleQuestionMenu="toggleQuestionMenu"
                            />
                        </template>
                        <div
                            v-if="$user.can('reuse questions')"
                            class="flex items-center justify-center text-center text-xs font-medium transition"
                        >
                            <router-link
                                :to="{name: 'questions.index', query: {launch: true}}"
                                type="button"
                                class="rounded-md border border-stone-300 bg-stone-50 px-8 py-4 dark:border-stone-700 dark:bg-stone-900"
                            >
                                + Add more questions
                            </router-link>
                        </div>
                        <Menu ref="questionMenuContainer" :popup="true" :model="questionMenuItems" />
                    </div>
                </div>
                <div ref="groupsBlock">
                    <!-- Hide group selection / publish form while reordering -->
                    <template v-if="selectedGroup && !reorderMode">
                        <Card class="mb-3 rounded-md border dark:border-stone-700">
                            <div class="flex">
                                <h3 class="mb-2 font-bold">Selected Group</h3>
                                <Button
                                    @click="resetGroup"
                                    icon="mdi:times"
                                    class="ml-auto h-8 w-8 bg-red-600 text-white"
                                />
                            </div>
                            <div class="flex grow items-end gap-2">
                                <GroupItem :group="selectedGroup" :showMenu="false" />
                                <div class="ml-auto flex h-full flex-col">
                                    <RouterLink
                                        :to="{
                                            name: 'feed',
                                            params: {group: selectedGroup.id},
                                        }"
                                        class="mt-6 text-sm font-semibold leading-none"
                                    >
                                        <Button
                                            class="h-8 w-8 border border-2 dark:border-stone-600"
                                            icon="akar-icons:chevron-right"
                                        />
                                    </RouterLink>
                                </div>
                            </div>
                        </Card>
                    </template>
                    <div v-auto-animate="{duration: 350}" v-if="!reorderMode">
                        <div v-if="!launch.group_id" class="relative pb-6" key="group">
                            <div class="mb-2">
                                <div class="mb-2 flex items-end">
                                    <h3 class="font-bold">Select a Group</h3>
                                    <RouterLink
                                        class="ml-auto flex-none"
                                        :to="{
                                            name: 'groups.create',
                                            query: {launch: true, question: currentRoute.params.question},
                                        }"
                                    >
                                        <Button
                                            :icon="{icon: 'mdi:plus', width: '16'}"
                                            rounded
                                            class="bg-askr-green !py-1 !pl-2 !pr-3 text-xs text-white"
                                        >
                                            Or create one
                                        </Button>
                                    </RouterLink>
                                </div>
                                <p class="text-xs">This will be the audience for your question(s)</p>
                            </div>
                            <InputText
                                type="search"
                                placeholder="Start typing to filter groups"
                                fluid
                                enterkeyhint="enter"
                                class="mb-2"
                                v-model="search.groups"
                            />
                            <GroupSelect v-model="launch.group_id" :groups="groups" class="mb-6" />
                            <div class="absolute -bottom-6 flex w-full justify-center">
                                <RouterLink :to="{name: 'groups.create', query: {launch: true}}">
                                    <Button
                                        :icon="{icon: 'mdi:plus', width: '28'}"
                                        rounded
                                        class="pointer-events-auto h-12 w-12 bg-askr-green text-white shadow shadow-stone-400"
                                    />
                                </RouterLink>
                            </div>
                        </div>
                        <div v-if="selectedGroup">
                            <form @submit.prevent="save">
                                <Card class="mb-12">
                                    <div class="mb-4 flex flex-col">
                                        <Label for="publish_from" class="mb-1" required>Publish from</Label>
                                        <DatePicker
                                            id="publish_from"
                                            name="publish_from"
                                            placeholder="Select a date to publish the question from"
                                            fluid
                                            showTime
                                            hourFormat="24"
                                            :min-date="new Date()"
                                            v-model="launch.publish_from"
                                            :hideOnDateTimeSelect="true"
                                            pt:pcinputtext:root:required
                                            dateFormat="dd/mm/yy"
                                        />
                                    </div>
                                    <div class="mb--4 flex flex-col">
                                        <Label for="publish_to" class="mb-1" required>Publish until</Label>
                                        <DatePicker
                                            id="publish_to"
                                            name="publish_to"
                                            placeholder="Select a date to publish the question until"
                                            fluid
                                            showTime
                                            hourFormat="24"
                                            :min-date="launch.publish_from ?? new Date()"
                                            v-model="launch.publish_to"
                                            :hideOnDateTimeSelect="true"
                                            pt:pcinputtext:root:required
                                            dateFormat="dd/mm/yy"
                                        />
                                    </div>
                                </Card>
                                <div class="flex justify-center">
                                    <button
                                        type="submit"
                                        class="pointer-events-auto flex h-16 w-16 flex-col items-center justify-center rounded-full bg-askr-green text-white shadow-xl shadow-stone-600 lg:h-24 lg:w-24 dark:shadow-stone-950"
                                    >
                                        <Icon icon="mingcute:send-plane-fill" class="h-8 w-8 lg:h-12 lg:w-12" />
                                        <span class="text-xs font-semibold">LAUNCH</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </template>
            <template v-else-if="!state.loading.questions">
                <div class="flex flex-col items-center py-10 text-center">
                    <h2 class="mb-8 font-bold">You haven't selected any questions to launch</h2>
                    <router-link
                        v-if="$user.can('reuse questions')"
                        :to="{name: 'questions.index', query: {launch: true}}"
                    >
                        <Button
                            icon="mdi:arrow-left"
                            class="mb-2 border"
                            rounded
                            :fluid="true"
                            :icon="{icon: 'mdi:arrow-left', width: '16'}"
                        >
                            Choose questions
                        </Button>
                    </router-link>
                </div>
            </template>
            <PageLoading v-if="state.loading.questions" />
        </div>
    </div>
</template>
<style scoped></style>
